import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Plus, Filter, CheckCircle2, Clock, AlertTriangle, Edit, Trash2, Printer, Users } from 'lucide-react';
import { addDays, format } from 'date-fns';

const toLocalInputValue = (dt) => {
  try {
    const d = new Date(dt);
    const tzOffset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
  } catch {
    return '';
  }
};

import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function Assessments() {
  const [assessments, setAssessments] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchCourseTitle, setSearchCourseTitle] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isRescheduleDialogOpen, setIsRescheduleDialogOpen] = useState(false);
  const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [completionDate, setCompletionDate] = useState('');
  const [completionResult, setCompletionResult] = useState('passed');
  const [completionNotes, setCompletionNotes] = useState('');
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [driverSearchDialog, setDriverSearchDialog] = useState('');
  const [newAssessment, setNewAssessment] = useState({
    driver_id: '',
    assessment_type: 'Yearly Peer-to-Peer Review',
    course_title: '',
    scheduled_date: '',
    notes: '',
    assessor: ''
  });


  const [dcpcCourses, setDcpcCourses] = useState([]);
  const [isDcpcDialogOpen, setIsDcpcDialogOpen] = useState(false);
  const [isEditDcpc, setIsEditDcpc] = useState(false);
  const [selectedDcpcCourse, setSelectedDcpcCourse] = useState(null);
  const [dcpcShiftPreset, setDcpcShiftPreset] = useState('day');
  const [dcpcSelectedDriverIds, setDcpcSelectedDriverIds] = useState([]);
  const [dcpcDriverSearch, setDcpcDriverSearch] = useState('');
  const [dcpcExternalAttendeesText, setDcpcExternalAttendeesText] = useState('');
  const [dcpcForm, setDcpcForm] = useState({
    module_name: '',
    location: '',
    trainer_name: '',
    start_datetime: '',
    end_datetime: '',
    status: 'planned',
    notes: ''
  });

  useEffect(() => {
    fetchCurrentUser();
    fetchData();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, getAuthHeaders());
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user');
    }
  };

  const fetchData = async () => {
    try {
      const [assessmentsRes, driversRes, dcpcRes] = await Promise.all([
        axios.get(`${API}/assessments`, getAuthHeaders()),
        axios.get(`${API}/drivers`, getAuthHeaders()),
        axios.get(`${API}/dcpc-courses`, getAuthHeaders())
      ]);
      
      const assessmentsWithDrivers = await Promise.all(
        assessmentsRes.data.map(async (assessment) => {
          const driver = driversRes.data.find(d => d.id === assessment.driver_id);
          return {
            ...assessment,
            driver_name: driver ? driver.name : 'Unknown'
          };
        })
      );
      
      setAssessments(assessmentsWithDrivers);
      setDrivers(driversRes.data);
      setDcpcCourses(dcpcRes.data || []);
    } catch (error) {
      toast.error('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const getDayRangeForSelectedDate = (date) => {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    const end = new Date(date);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  };

  const dcpcCoursesForSelectedDate = useMemo(() => {
    const { start, end } = getDayRangeForSelectedDate(selectedDate);
    return (dcpcCourses || [])
      .filter((c) => {
        const startDt = new Date(c.start_datetime);
        return startDt >= start && startDt <= end;
      })
      .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
  }, [dcpcCourses, selectedDate]);

  const dcpcDatesSet = useMemo(() => {
    const set = new Set();
    for (const c of dcpcCourses || []) {
      const d = new Date(c.start_datetime);
      // normalize to YYYY-MM-DD in local time
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      set.add(key);
    }
    return set;
  }, [dcpcCourses]);

  const applyDcpcPreset = (preset, baseDate) => {
    const d = new Date(baseDate);
    const start = new Date(d);
    const end = new Date(d);

    if (preset === 'night') {
      start.setHours(21, 0, 0, 0);
      end.setHours(5, 0, 0, 0);
      end.setDate(end.getDate() + 1);
    } else {
      start.setHours(6, 0, 0, 0);
      end.setHours(14, 0, 0, 0);
    }

    setDcpcForm((prev) => ({
      ...prev,
      start_datetime: start.toISOString().slice(0, 16),
      end_datetime: end.toISOString().slice(0, 16),
    }));
  };

  const openCreateDcpcDialog = () => {
    setIsEditDcpc(false);
    setSelectedDcpcCourse(null);
    setDcpcShiftPreset('day');
    setDcpcSelectedDriverIds([]);
    setDcpcExternalAttendeesText('');
    setDcpcDriverSearch('');

    setDcpcForm({
      module_name: '',
      location: '',
      trainer_name: '',
      start_datetime: '',
      end_datetime: '',
      status: 'planned',
      notes: ''
    });

    applyDcpcPreset('day', selectedDate);
    setIsDcpcDialogOpen(true);
  };

  const openEditDcpcDialog = (course) => {
    setIsEditDcpc(true);
    setSelectedDcpcCourse(course);
    setDcpcSelectedDriverIds(course.internal_driver_ids || []);
    setDcpcExternalAttendeesText((course.external_attendees || []).join('\n'));

    const startDt = new Date(course.start_datetime);
    const endDt = new Date(course.end_datetime);

    // infer preset
    const preset = startDt.getHours() === 21 ? 'night' : 'day';
    setDcpcShiftPreset(preset);

    setDcpcForm({
      module_name: course.module_name || '',
      location: course.location || '',
      trainer_name: course.trainer_name || '',
      start_datetime: toLocalInputValue(course.start_datetime),
      end_datetime: toLocalInputValue(course.end_datetime),
      status: course.status || 'planned',
      notes: course.notes || ''
    });

    setIsDcpcDialogOpen(true);
  };

  const handleSubmitDcpcCourse = async (e) => {
    e.preventDefault();

    if (!dcpcForm.module_name || !dcpcForm.location || !dcpcForm.trainer_name || !dcpcForm.start_datetime || !dcpcForm.end_datetime) {
      toast.error('Please fill module, location, trainer, start and end time');
      return;
    }

    const external_attendees = dcpcExternalAttendeesText
      .split('\n')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const payload = {
      module_name: dcpcForm.module_name,
      location: dcpcForm.location,
      trainer_name: dcpcForm.trainer_name,
      start_datetime: new Date(dcpcForm.start_datetime).toISOString(),
      end_datetime: new Date(dcpcForm.end_datetime).toISOString(),
      status: dcpcForm.status,
      internal_driver_ids: dcpcSelectedDriverIds,
      external_attendees,
      notes: dcpcForm.notes,
    };

    try {
      if (isEditDcpc && selectedDcpcCourse?.id) {
        await axios.put(`${API}/dcpc-courses/${selectedDcpcCourse.id}`, payload, getAuthHeaders());
        toast.success('DCPC course updated');
      } else {
        await axios.post(`${API}/dcpc-courses`, payload, getAuthHeaders());
        toast.success('DCPC course scheduled');
      }

      setIsDcpcDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to save DCPC course');
    }
  };

  const handleDeleteDcpcCourse = async (courseId) => {
    if (!window.confirm('Delete this DCPC course? This will remove linked driver assessments too.')) return;
    try {
      await axios.delete(`${API}/dcpc-courses/${courseId}`, getAuthHeaders());
      toast.success('DCPC course deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete DCPC course');
    }
  };

  const handleAddAssessment = async (e) => {
    e.preventDefault();
    
    const driversToSchedule = selectedDrivers.length > 0 ? selectedDrivers : [newAssessment.driver_id];
    
    if (driversToSchedule.length === 0 || (driversToSchedule.length === 1 && !driversToSchedule[0])) {
      toast.error('Please select at least one driver');
      return;
    }
    
    try {
      // Create assessment for each selected driver
      const promises = driversToSchedule.map(driverId => {
        const payload = {
          driver_id: driverId,
          assessment_type: newAssessment.assessment_type,
          course_title: newAssessment.course_title,
          scheduled_date: new Date(newAssessment.scheduled_date).toISOString(),
          notes: newAssessment.notes,
          assessor: newAssessment.assessor
        };
        return axios.post(`${API}/assessments`, payload, getAuthHeaders());
      });
      
      await Promise.all(promises);
      
      toast.success(`Assessment scheduled for ${driversToSchedule.length} driver(s)`);
      
      // Close dialog and reset form
      setIsDialogOpen(false);
      setSelectedDrivers([]);
      setTimeout(() => {
        setNewAssessment({
          driver_id: '',
          assessment_type: 'Yearly Peer-to-Peer Review',
          course_title: '',
          scheduled_date: '',
          notes: '',
          assessor: ''
        });
      }, 300);
      
      fetchData();
    } catch (error) {
      toast.error('Failed to schedule assessment');
    }
  };

  const toggleDriverSelection = (driverId) => {
    setSelectedDrivers(prev => 
      prev.includes(driverId) 
        ? prev.filter(id => id !== driverId)
        : [...prev, driverId]
    );
  };

  const handleOpenCompleteAssessment = (assessment) => {
    setSelectedAssessment(assessment);
    setCompletionDate(format(new Date(), 'yyyy-MM-dd'));
    setCompletionResult('passed');
    setCompletionNotes('');
    setIsCompleteDialogOpen(true);
  };

  const handleCompleteAssessment = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(
        `${API}/assessments/${selectedAssessment.id}`,
        {
          status: 'completed',
          completed_date: new Date(completionDate).toISOString(),
          result: completionResult,
          completion_notes: completionNotes
        },
        getAuthHeaders()
      );
      toast.success(`Assessment marked as ${completionResult}`);
      setIsCompleteDialogOpen(false);
      setSelectedAssessment(null);
      setCompletionDate('');
      setCompletionResult('passed');
      setCompletionNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update assessment');
    }
  };

  const handleDeleteAssessment = async (assessmentId) => {
    if (!window.confirm('Are you sure you want to delete this assessment?')) return;
    
    try {
      await axios.delete(`${API}/assessments/${assessmentId}`, getAuthHeaders());
      toast.success('Assessment deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete assessment');
    }
  };

  const handleOpenReschedule = (assessment) => {
    setSelectedAssessment(assessment);
    setRescheduleDate(format(new Date(assessment.scheduled_date), "yyyy-MM-dd'T'HH:mm"));
    setIsRescheduleDialogOpen(true);
  };

  const handleRescheduleAssessment = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(
        `${API}/assessments/${selectedAssessment.id}`,
        {
          scheduled_date: new Date(rescheduleDate).toISOString()
        },
        getAuthHeaders()
      );
      toast.success('Assessment rescheduled successfully');
      setIsRescheduleDialogOpen(false);
      setSelectedAssessment(null);
      setRescheduleDate('');
      fetchData();
    } catch (error) {
      toast.error('Failed to reschedule assessment');
    }
  };

  const canManageAssessments = currentUser?.role === 'admin' || currentUser?.role === 'coach';

  const filteredAssessments = (() => {
    // For P2P compliance filters, we need to show DRIVERS not just assessments
    if (filterStatus.startsWith('p2p-')) {
      const now = new Date();
      const oneYearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
      const twoYearsAgo = new Date(now.getTime() - 730 * 24 * 60 * 60 * 1000);
      
      const driverP2PStatus = [];
      
      // Calculate P2P status for each driver
      drivers.forEach(driver => {
        const driverP2PAssessments = assessments.filter(a => 
          a.driver_id === driver.id && 
          a.assessment_type === 'Yearly Peer-to-Peer Review'
        );
        
        const completedP2P = driverP2PAssessments
          .filter(a => a.status === 'completed' && a.completed_date)
          .sort((a, b) => new Date(b.completed_date) - new Date(a.completed_date));
        
        const scheduledP2P = driverP2PAssessments.find(a => 
          a.status === 'scheduled' || a.status === 'due' || a.status === 'overdue'
        );
        
        let driverStatus = 'overdue';
        let displayAssessment = null;
        
        if (completedP2P.length > 0) {
          const lastCompleted = new Date(completedP2P[0].completed_date);
          if (lastCompleted > oneYearAgo) {
            driverStatus = 'compliant';
            displayAssessment = completedP2P[0];
          } else if (lastCompleted > twoYearsAgo) {
            driverStatus = 'due';
            displayAssessment = scheduledP2P || completedP2P[0];
          } else {
            driverStatus = 'overdue';
            displayAssessment = scheduledP2P || completedP2P[0];
          }
        } else {
          // No completed P2P - always overdue
          driverStatus = 'overdue';
          displayAssessment = scheduledP2P;
        }
        
        // If no assessment exists, create virtual one
        if (!displayAssessment) {
          displayAssessment = {
            id: `virtual-${driver.id}`,
            driver_id: driver.id,
            driver_name: driver.name,
            assessment_type: 'Yearly Peer-to-Peer Review',
            status: driverStatus,
            scheduled_date: new Date().toISOString(),
            notes: driverStatus === 'overdue' ? 'No P2P scheduled - OVERDUE' : 'No P2P scheduled',
            is_virtual: true
          };
        } else {
          displayAssessment = { ...displayAssessment, calculated_status: driverStatus };
        }
        
        driverP2PStatus.push({ driver, status: driverStatus, assessment: displayAssessment });
      });
      
      // Filter by selected P2P status
      const filtered = driverP2PStatus.filter(item => {
        if (filterStatus === 'p2p-compliant') return item.status === 'compliant';
        if (filterStatus === 'p2p-due') return item.status === 'due';
        if (filterStatus === 'p2p-overdue') return item.status === 'overdue';
        return true;
      });
      
      return filtered.map(item => item.assessment);
    }
    
    // Regular filtering for non-P2P filters
    return assessments.filter((assessment) => {
      // Filter by status
      if (filterStatus !== 'all' && !filterStatus.startsWith('p2p-') && assessment.status !== filterStatus) {
        return false;
      }
      
      // Filter by type
      if (filterType !== 'all' && assessment.assessment_type !== filterType) {
        return false;
      }
      
      // Filter by course/intervention title
      if (searchCourseTitle) {
        if (!assessment.course_title || 
            !assessment.course_title.toLowerCase().includes(searchCourseTitle.toLowerCase())) {
          return false;
        }
      }
      
      return true;
    });
  })();

  const getAssessmentsForDate = (date) => {
    return assessments.filter((assessment) => {
      const assessmentDate = new Date(assessment.scheduled_date);
      return assessmentDate.toDateString() === date.toDateString();
    });
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return <div className="text-center py-12">Loading assessments...</div>;
  }

  return (
    <div className="space-y-8" data-testid="assessments-page">
      {/* Print Title - Only shows when printing */}
      <div className="hidden print:block mb-8 border-b pb-4">
        <h1 className="text-3xl font-bold text-slate-800">Assessment List</h1>
        <p className="text-slate-600">Generated on {format(new Date(), 'MMMM d, yyyy')}</p>
        <p className="text-slate-600">Total: {filteredAssessments.length} assessments</p>
      </div>

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-slate-100 mb-2">Assessments</h1>
          <p className="text-slate-600 dark:text-slate-400">Schedule and manage driver assessments</p>
        </div>
        <div className="flex space-x-2 no-print">
          <Button variant="outline" onClick={handlePrint} data-testid="print-assessments-button">
            <Printer className="h-4 w-4 mr-2" />
            Print List
          </Button>
          {canManageAssessments && (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="schedule-assessment-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Schedule Assessment
                </Button>
              </DialogTrigger>
          <DialogContent data-testid="schedule-assessment-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Schedule New Assessment</DialogTitle>
              <DialogDescription>
                Schedule assessment for one or multiple drivers (e.g., group training session)
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddAssessment} className="space-y-4">
              <div className="space-y-2">
                <Label>Select Driver(s) *</Label>
                <p className="text-xs text-slate-600 mb-2">
                  Select multiple drivers for group sessions, or single driver for individual assessment
                </p>
                <Input
                  type="text"
                  placeholder="Search drivers by name or ID..."
                  value={driverSearchDialog}
                  onChange={(e) => setDriverSearchDialog(e.target.value)}
                  className="mb-2"
                />
                <div className="border rounded-md p-3 max-h-60 overflow-y-auto space-y-2">
                  {drivers
                    .filter(driver =>
                      driver.name.toLowerCase().includes(driverSearchDialog.toLowerCase()) ||
                      (driver.employee_id && driver.employee_id.toLowerCase().includes(driverSearchDialog.toLowerCase()))
                    )
                    .map((driver) => (
                    <label key={driver.id} className="flex items-center space-x-2 cursor-pointer hover:bg-slate-50 p-2 rounded">
                      <input
                        type="checkbox"
                        checked={selectedDrivers.includes(driver.id)}
                        onChange={() => toggleDriverSelection(driver.id)}
                        className="rounded w-4 h-4"
                      />
                      <span className="text-sm text-slate-800">
                        {driver.name} {driver.employee_id ? `(${driver.employee_id})` : ''}
                      </span>
                    </label>
                  ))}
                </div>
                {selectedDrivers.length > 0 && (
                  <p className="text-sm text-green-600 font-medium">
                    ✓ {selectedDrivers.length} driver(s) selected
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Assessment Type *</Label>
                <select
                  id="type"
                  data-testid="assessment-type-select"
                  className="w-full px-3 py-2 border rounded-md"
                  value={newAssessment.assessment_type}
                  onChange={(e) => setNewAssessment({ ...newAssessment, assessment_type: e.target.value })}
                >
                  <option value="Yearly Peer-to-Peer Review">Yearly Peer-to-Peer Review</option>
                  <option value="Risk Assessment">Risk Assessment</option>
                  <option value="New Driver Induction">New Driver Induction</option>
                  <option value="Training Course">Training Course</option>
                  <option value="Coaching Intervention">Coaching Intervention</option>
                  <option value="Coaching Ride Along">Coaching Ride Along</option>
                </select>
              </div>
              {newAssessment.assessment_type === 'Training Course' && (
                <div className="space-y-2">
                  <Label htmlFor="course">Course Title *</Label>
                  <Input
                    id="course"
                    data-testid="course-title-input"
                    value={newAssessment.course_title}
                    onChange={(e) => setNewAssessment({ ...newAssessment, course_title: e.target.value })}
                    required={newAssessment.assessment_type === 'Training Course'}
                  />
                </div>
              )}
              {newAssessment.assessment_type === 'Coaching Intervention' && (
                <div className="space-y-2">
                  <Label htmlFor="intervention-title">Intervention Title</Label>
                  <Input
                    id="intervention-title"
                    data-testid="intervention-title-input"
                    value={newAssessment.course_title}
                    onChange={(e) => setNewAssessment({ ...newAssessment, course_title: e.target.value })}
                    placeholder="e.g., Reversing Safety, Distraction Management"
                  />
                </div>
              )}
              {newAssessment.assessment_type === 'Coaching Ride Along' && (
                <div className="space-y-2">
                  <Label htmlFor="ridealong-title">Ride Along Focus</Label>
                  <Input
                    id="ridealong-title"
                    data-testid="ridealong-title-input"
                    value={newAssessment.course_title}
                    onChange={(e) => setNewAssessment({ ...newAssessment, course_title: e.target.value })}
                    placeholder="e.g., Route Assessment, Delivery Observation"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="date">Scheduled Date & Time *</Label>
                <Input
                  id="date"
                  data-testid="scheduled-date-input"
                  type="datetime-local"
                  value={newAssessment.scheduled_date}
                  onChange={(e) => setNewAssessment({ ...newAssessment, scheduled_date: e.target.value })}
                  required
                />
                <p className="text-xs text-slate-500">Select date and time for assessment</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="assessor">
                  {newAssessment.assessment_type === 'Training Course' ? 'Trainer' : 
                   newAssessment.assessment_type === 'Coaching Intervention' ? 'Coach' :
                   newAssessment.assessment_type === 'Coaching Ride Along' ? 'Coach' : 'Assessor'}
                </Label>
                <Input
                  id="assessor"
                  data-testid="assessor-input"
                  value={newAssessment.assessor}
                  onChange={(e) => setNewAssessment({ ...newAssessment, assessor: e.target.value })}
                  placeholder={
                    newAssessment.assessment_type === 'Training Course' ? 'Enter trainer name' : 
                    newAssessment.assessment_type === 'Coaching Intervention' ? 'Enter coach name' :
                    newAssessment.assessment_type === 'Coaching Ride Along' ? 'Enter coach name' :
                    'Enter assessor name'
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  data-testid="notes-input"
                  value={newAssessment.notes}
                  onChange={(e) => setNewAssessment({ ...newAssessment, notes: e.target.value })}
                />
              </div>
              <Button type="submit" className="w-full" data-testid="submit-assessment-button">
                Schedule Assessment
              </Button>
            </form>
          </DialogContent>
        </Dialog>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <Card className="lg:col-span-2" data-testid="assessments-list-card">
          <CardHeader>
            <div className="flex flex-col space-y-3">
              <div className="flex items-center justify-between">
                <CardTitle>All Assessments</CardTitle>
                <div className="flex items-center space-x-2 no-print">
                  <Filter className="h-4 w-4 text-slate-600" />
                  <select
                    data-testid="filter-status-select"
                    className="px-3 py-1 border rounded-md text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="completed">Completed</option>
                    <option value="p2p-compliant">P2P: Compliant (within 1 year)</option>
                    <option value="p2p-due">P2P: Due (1-2 years)</option>
                    <option value="p2p-overdue">P2P: Overdue (2+ years)</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center justify-between no-print">
                <p className="text-sm text-slate-600">Filter by Type:</p>
                <select
                  data-testid="filter-type-select"
                  className="px-3 py-1 border rounded-md text-sm"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="all">All Types</option>
                  <option value="Yearly Peer-to-Peer Review">Yearly Peer-to-Peer Review</option>
                  <option value="Risk Assessment">Risk Assessment</option>
                  <option value="New Driver Induction">New Driver Induction</option>
                  <option value="Training Course">Training Course</option>
                  <option value="Coaching Intervention">Coaching Intervention</option>
                  <option value="Coaching Ride Along">Coaching Ride Along</option>
                </select>
              </div>
              <div className="flex items-center space-x-2 no-print">
                <Input
                  type="text"
                  placeholder="Search by course/intervention title..."
                  value={searchCourseTitle}
                  onChange={(e) => setSearchCourseTitle(e.target.value)}
                  className="max-w-sm"
                  data-testid="search-course-title-input"
                />
              </div>
              <div className="text-sm text-slate-600">
                Showing {filteredAssessments.length} of {assessments.length} assessments
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredAssessments.length === 0 ? (
              <p className="text-slate-500 text-sm">No assessments found</p>
            ) : (
              <div className="space-y-4">
                {filteredAssessments.map((assessment) => (
                  <div
                    key={assessment.id}
                    className="p-4 border rounded-lg hover:shadow-md transition-shadow page-break-inside-avoid"
                    data-testid={`assessment-item-${assessment.id}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium text-slate-800">{assessment.driver_name}</p>
                        <p className="text-sm text-slate-600">{assessment.assessment_type}</p>
                        {assessment.course_title && (
                          <p className="text-sm text-slate-500">{assessment.course_title}</p>
                        )}
                      </div>
                      <Badge
                        variant={
                          assessment.status === 'completed'
                            ? 'default'
                            : assessment.status === 'overdue'
                            ? 'destructive'
                            : assessment.status === 'due'
                            ? 'secondary'
                            : 'secondary'
                        }
                        className={
                          assessment.status === 'due' ? 'bg-amber-100 text-amber-800 border-amber-300' : ''
                        }
                        data-testid={`assessment-badge-${assessment.id}`}
                      >
                        {assessment.status === 'completed' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                        {assessment.status === 'scheduled' && <Clock className="h-3 w-3 mr-1" />}
                        {assessment.status === 'due' && <AlertTriangle className="h-3 w-3 mr-1" />}
                        {assessment.status === 'overdue' && <AlertTriangle className="h-3 w-3 mr-1" />}
                        {assessment.status}
                      </Badge>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center space-x-4 text-sm text-slate-600">
                        <span>📅 Scheduled: {format(new Date(assessment.scheduled_date), 'MMM d, yyyy')}</span>
                        {assessment.assessor && <span>👤 {assessment.assessor}</span>}
                      </div>
                      {assessment.status === 'completed' && assessment.completed_date && (
                        <div className="flex items-center text-sm text-slate-600">
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          Completed: {format(new Date(assessment.completed_date), 'MMM d, yyyy')}
                        </div>
                      )}
                      {assessment.result && (
                        <div className={`text-sm px-2 py-1 rounded ${
                          assessment.result === 'passed' 
                            ? 'bg-green-50 text-green-700 border border-green-200' 
                            : 'bg-red-50 text-red-700 border border-red-200'
                        }`}>
                          {assessment.result === 'passed' ? '✓ Passed' : '✗ Failed'}
                        </div>
                      )}
                      {assessment.completion_notes && (
                        <div className="bg-slate-100 px-2 py-1 rounded text-sm">
                          <p className="text-xs font-medium text-slate-700">Comments:</p>
                          <p className="text-xs text-slate-600">{assessment.completion_notes}</p>
                        </div>
                      )}
                      {assessment.last_p2p_completion && (
                        <div className="text-sm text-amber-700 bg-amber-50 px-2 py-1 rounded">
                          📌 Last P2P completed: {format(new Date(assessment.last_p2p_completion), 'MMM d, yyyy')} 
                          <span className="ml-2 font-medium">
                            (Gap: {Math.floor((new Date(assessment.scheduled_date) - new Date(assessment.last_p2p_completion)) / (1000 * 60 * 60 * 24))} days)
                          </span>
                        </div>
                      )}
                      {!assessment.last_p2p_completion && assessment.assessment_type === 'Yearly Peer-to-Peer Review' && (
                        <div className="text-sm text-red-600 bg-red-50 px-2 py-1 rounded">
                          ⚠️ First P2P assessment for this driver
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div></div>
                      <div className="flex space-x-2">
                        {canManageAssessments && (assessment.status === 'scheduled' || assessment.status === 'overdue' || assessment.status === 'due') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenCompleteAssessment(assessment)}
                            data-testid={`complete-btn-${assessment.id}`}
                          >
                            Complete
                          </Button>
                        )}
                        {canManageAssessments && (assessment.status === 'scheduled' || assessment.status === 'overdue' || assessment.status === 'due') && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenReschedule(assessment)}
                              data-testid={`reschedule-btn-${assessment.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteAssessment(assessment.id)}
                              data-testid={`delete-btn-${assessment.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {currentUser?.role === 'admin' && assessment.status === 'completed' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteAssessment(assessment.id)}
                            data-testid={`delete-completed-btn-${assessment.id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                    {assessment.notes && (
                      <p className="text-sm text-slate-600 mt-2">{assessment.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="calendar-view-card">
          <CardHeader>
            <CardTitle>Calendar View</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(d) => {
                if (d) setSelectedDate(d);
              }}
              modifiers={{
                dcpc: (date) => {
                  const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                  return dcpcDatesSet.has(key);
                }
              }}
              modifiersClassNames={{
                dcpc: 'ring-2 ring-purple-500 ring-offset-1'
              }}
              className="rounded-md border"
              data-testid="assessments-calendar"
            />
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <h4 className="font-medium text-slate-800">Assessments on {format(selectedDate, 'MMM d, yyyy')}</h4>
                {(currentUser?.role === 'admin' || currentUser?.role === 'coach') && (
                  <Button size="sm" variant="outline" onClick={openCreateDcpcDialog} data-testid="open-dcpc-dialog">
                    <Plus className="h-4 w-4 mr-2" />
                    Schedule DCPC Course
                  </Button>
                )}
              </div>

              {/* DCPC courses for this day */}
              <div className="space-y-2" data-testid="dcpc-courses-day-list">
                <p className="text-sm font-medium text-purple-700">DCPC Courses</p>
                {dcpcCoursesForSelectedDate.length === 0 ? (
                  <p className="text-slate-500 text-sm">No DCPC courses</p>
                ) : (
                  <div className="space-y-2">
                    {dcpcCoursesForSelectedDate.map((c) => (
                      <div key={c.id} className="p-3 rounded border border-purple-300 bg-purple-50 text-sm" data-testid={`dcpc-course-${c.id}`}>


                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-800">DCPC: {c.module_name}</p>
                            <p className="text-slate-600">{c.location} • {c.trainer_name}</p>
                            <p className="text-slate-600">
                              {format(new Date(c.start_datetime), 'HH:mm')}–{format(new Date(c.end_datetime), 'HH:mm')} • {c.status}
                            </p>

                            {/* Attendees */}
                            <div className="mt-2 space-y-1 text-xs">
                              <div className="flex items-center gap-2 text-purple-700 font-medium">
                                <Users className="h-4 w-4" />
                                Attendees
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {(c.internal_driver_ids || []).map((id) => {
                                  const d = drivers.find((x) => x.id === id);
                                  const label = d ? `${d.name} (${d.employee_id || '—'})` : id;
                                  return (
                                    <span key={id} className="px-2 py-1 rounded bg-white border border-purple-200 text-slate-700">
                                      {label}
                                    </span>
                                  );
                                })}
                                {(c.external_attendees || []).map((name, idx) => (
                                  <span key={`${c.id}-ext-${idx}`} className="px-2 py-1 rounded bg-white border border-purple-200 text-slate-700">
                                    {name}
                                  </span>
                                ))}
                              </div>
                              <p className="text-slate-600">
                                Internal: {(c.internal_driver_ids || []).length} • External: {(c.external_attendees || []).length}
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-col gap-2">
                            {c.status !== 'completed' && (
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={async () => {
                                  try {
                                    await axios.put(`${API}/dcpc-courses/${c.id}`, { status: 'completed' }, getAuthHeaders());
                                    toast.success('Marked completed');
                                    fetchData();
                                  } catch {
                                    toast.error('Failed to mark completed');
                                  }
                                }}
                              >
                                <CheckCircle2 className="h-4 w-4 mr-1" />
                                Complete
                              </Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => openEditDcpcDialog(c)}>
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleDeleteDcpcCourse(c.id)}>
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Regular assessments for this day */}
              {getAssessmentsForDate(selectedDate).length === 0 ? (
                <p className="text-slate-500 text-sm">No assessments scheduled</p>
              ) : (
                <div className="space-y-2">
                  {getAssessmentsForDate(selectedDate).map((assessment) => (
                    <div key={assessment.id} className="p-2 bg-slate-50 rounded text-sm" data-testid={`calendar-assessment-${assessment.id}`}>
                      <p className="font-medium">{assessment.driver_name}</p>
                      <p className="text-slate-600">{assessment.assessment_type}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reschedule Dialog */}
      <Dialog open={isRescheduleDialogOpen} onOpenChange={setIsRescheduleDialogOpen}>
        <DialogContent data-testid="reschedule-dialog">
          <DialogHeader>
            <DialogTitle>Reschedule Assessment</DialogTitle>
            <DialogDescription>
              Change the scheduled date for this assessment.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRescheduleAssessment} className="space-y-4">
            {selectedAssessment && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-800">{selectedAssessment.driver_name}</p>
                <p className="text-sm text-slate-600">{selectedAssessment.assessment_type}</p>
                {selectedAssessment.course_title && (
                  <p className="text-sm text-slate-500">{selectedAssessment.course_title}</p>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="reschedule-date">New Scheduled Date & Time *</Label>
              <Input
                id="reschedule-date"
                data-testid="reschedule-date-input"
                type="datetime-local"
                value={rescheduleDate}
                onChange={(e) => setRescheduleDate(e.target.value)}
                required
              />
            </div>
            <div className="flex space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => setIsRescheduleDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1" data-testid="confirm-reschedule-button">
                Reschedule
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Complete Assessment Dialog */}
      <Dialog open={isCompleteDialogOpen} onOpenChange={setIsCompleteDialogOpen}>
        <DialogContent data-testid="complete-assessment-dialog">
          <DialogHeader>
            <DialogTitle>Complete Assessment</DialogTitle>
            <DialogDescription>
              Select the date when this assessment was completed.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCompleteAssessment} className="space-y-4">
            {selectedAssessment && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-800">{selectedAssessment.driver_name}</p>
                <p className="text-sm text-slate-600">{selectedAssessment.assessment_type}</p>
                {selectedAssessment.course_title && (
                  <p className="text-sm text-slate-500">{selectedAssessment.course_title}</p>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="completion-date-assess">Completion Date *</Label>
              <Input
                id="completion-date-assess"
                data-testid="completion-date-input"
                type="date"
                value={completionDate}
                onChange={(e) => setCompletionDate(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="result-assess">Result *</Label>
              <select
                id="result-assess"
                data-testid="completion-result-select"
                className="w-full px-3 py-2 border rounded-md"
                value={completionResult}
                onChange={(e) => setCompletionResult(e.target.value)}
              >
                <option value="passed">Passed</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="completion-notes-assess">Comments</Label>
              <Textarea
                id="completion-notes-assess"
                data-testid="completion-notes-input"
                placeholder="Add any comments about this assessment (required if failed)"
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                rows={3}
                required={completionResult === 'failed'}
              />
              {completionResult === 'failed' && (
                <p className="text-xs text-red-600">
                  Comments are required when marking an assessment as failed
                </p>
              )}
            </div>
            <div className="flex space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => setIsCompleteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1" data-testid="confirm-complete-button">
                Mark as {completionResult === 'passed' ? 'Passed' : 'Failed'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>


      {/* DCPC Create/Edit Dialog (must be outside lists so the button always works) */}
      <Dialog open={isDcpcDialogOpen} onOpenChange={setIsDcpcDialogOpen}>
        <DialogContent
          className="w-[95vw] sm:max-w-3xl max-h-[85vh] overflow-y-auto"
          data-testid="dcpc-dialog"
        >
          <DialogHeader>
            <DialogTitle>{isEditDcpc ? 'Edit DCPC Course' : 'Schedule DCPC Course'}</DialogTitle>
            <DialogDescription>
              Create a planned DCPC course and assign multiple drivers (tick boxes) plus any external attendees.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitDcpcCourse} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Module name *</Label>
                <Input
                  value={dcpcForm.module_name}
                  onChange={(e) => setDcpcForm({ ...dcpcForm, module_name: e.target.value })}
                  placeholder="e.g., Module 1"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Location *</Label>
                <Input
                  value={dcpcForm.location}
                  onChange={(e) => setDcpcForm({ ...dcpcForm, location: e.target.value })}
                  placeholder="e.g., Depot A"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Trainer / Coach name *</Label>
                <Input
                  value={dcpcForm.trainer_name}
                  onChange={(e) => setDcpcForm({ ...dcpcForm, trainer_name: e.target.value })}
                  placeholder="Trainer name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <select
                  className="w-full px-3 py-2 border dark:border-slate-700 rounded-md"
                  value={dcpcForm.status}
                  onChange={(e) => setDcpcForm({ ...dcpcForm, status: e.target.value })}
                >
                  <option value="planned">planned</option>
                  <option value="completed">completed</option>
                  <option value="cancelled">cancelled</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Label className="mr-2">Shift preset:</Label>
              <Button
                type="button"
                size="sm"
                variant={dcpcShiftPreset === 'day' ? 'default' : 'outline'}
                onClick={() => {
                  setDcpcShiftPreset('day');
                  applyDcpcPreset('day', selectedDate);
                }}
              >
                Day (06:00–14:00)
              </Button>
              <Button
                type="button"
                size="sm"
                variant={dcpcShiftPreset === 'night' ? 'default' : 'outline'}
                onClick={() => {
                  setDcpcShiftPreset('night');
                  applyDcpcPreset('night', selectedDate);
                }}
              >
                Night (21:00–05:00)
              </Button>
              <p className="text-xs text-slate-500 ml-2">You can override times below.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start (date & time) *</Label>
                <Input
                  type="datetime-local"
                  value={dcpcForm.start_datetime}
                  onChange={(e) => setDcpcForm({ ...dcpcForm, start_datetime: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>End (date & time) *</Label>
                <Input
                  type="datetime-local"
                  value={dcpcForm.end_datetime}
                  onChange={(e) => setDcpcForm({ ...dcpcForm, end_datetime: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={dcpcForm.notes} onChange={(e) => setDcpcForm({ ...dcpcForm, notes: e.target.value })} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Internal drivers (tick boxes)</Label>
                <Input
                  placeholder="Search drivers..."
                  value={dcpcDriverSearch}
                  onChange={(e) => setDcpcDriverSearch(e.target.value)}
                />
                <div className="max-h-64 overflow-y-auto border rounded p-2 space-y-1">
                  {drivers
                    .filter((d) => d.name.toLowerCase().includes(dcpcDriverSearch.toLowerCase()) || (d.employee_id || '').includes(dcpcDriverSearch))
                    .map((d) => (
                      <label key={d.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={dcpcSelectedDriverIds.includes(d.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setDcpcSelectedDriverIds((prev) => [...prev, d.id]);
                            } else {
                              setDcpcSelectedDriverIds((prev) => prev.filter((x) => x !== d.id));
                            }
                          }}
                        />
                        <span className="text-slate-800">{d.name}</span>
                        <span className="text-xs text-slate-500">({d.employee_id || '—'})</span>
                      </label>
                    ))}
                </div>
                <p className="text-xs text-slate-500">Selected: {dcpcSelectedDriverIds.length}</p>
              </div>

              <div className="space-y-2">
                <Label>External / visiting attendees (one per line)</Label>
                <Textarea
                  value={dcpcExternalAttendeesText}
                  onChange={(e) => setDcpcExternalAttendeesText(e.target.value)}
                  placeholder="Visitor name 1\nVisitor name 2"
                  className="min-h-[220px]"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsDcpcDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {isEditDcpc ? 'Save changes' : 'Schedule course'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
}